import { Box, Paper } from "@material-ui/core";
import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import { TypedNegotiationRound } from "../../../models/flexible/typed-negotiation-round";
import { AgreementUserRole, Case } from "../../../models/generated/graphql";
import startUpper from "../../../utils/start-upper";
import { GetCaseByIdQuery } from "../types/queries.generated";
import useStyles from "../use-styles";
import CaseInformation from "./CaseInformation";
import CaseInformationDetails, { ICaseInformationDetails } from "./CaseInformationDetails";
import CaseReview from "./CaseReview";
import CollapsibleFormCard from "./CollapsibleFormCard";
import FormCard, { IFormsCard } from "./FormCard";
import Forms from "./Forms";
import RecordsCard, { Record } from "./RecordsCard";
import TreatmentSummary from "./TreatmentSummary";
import { ITreatmentSummaryDetails } from "./TreatmentSummaryDetails";

interface IBody {
	negotiationRounds: Array<TypedNegotiationRound>;
	caseInformation?: ICaseInformationDetails | null | undefined;
	treatmentSummary?: ITreatmentSummaryDetails | null | undefined;
	forms?: Array<Array<IFormsCard>> | null | undefined;
	isFormsCreatable?: boolean | null | undefined;
	dataGuidanceForms?: Array<IFormsCard> | null | undefined;
	records: Array<Record>;
	currency: NonNullable<GetCaseByIdQuery["cases"][number]["baseAgreement"]>["currency"] | undefined;
	isStob?: boolean | null | undefined;
	organizationId: string;
	role: AgreementUserRole | null | undefined;
	caseInstance: Case;
	isDrawerOpen: boolean;
	canUserViewFinancials: boolean;
	refetch: any;
}

const Body = (props: IBody) => {
	const { bodyRoot } = useStyles();
	const { t } = useTranslation();
	const currencyName = t(props.currency?.Name || "") || "";

	return (
		<>
			<Paper classes={{ rounded: bodyRoot }}>
				{!!props.negotiationRounds.length && (
					<CaseReview
						negotiationRounds={props.negotiationRounds}
						organizationId={props.organizationId}
						role={props.role}
						caseInstance={props.caseInstance}
					/>
				)}
			</Paper>
			<Paper classes={{ rounded: bodyRoot }}>
				<Box paddingBottom={2}>
					<CaseInformation
						title={startUpper(t("case-info"))}
						status={props.caseInstance.status}
						patientAttributes={props.caseInformation?.patientAttributes}
					>
						<CaseInformationDetails {...props?.caseInformation} />
					</CaseInformation>
				</Box>
			</Paper>
			<Paper classes={{ rounded: bodyRoot }}>
				<TreatmentSummary
					title={startUpper(t("treatment-summary"))}
					treatmentSummary={props.treatmentSummary}
					currencyName={currencyName}
					records={props.records}
				/>
			</Paper>
			<Paper classes={{ rounded: bodyRoot }}>
				<Forms>
					{props?.forms?.map((form, index) => (
						<Fragment key={index}>
							<CollapsibleFormCard
								forms={form}
								isFormOwner={props.caseInformation?.isFormOwner}
								isFormCreatable={props.isFormsCreatable}
							/>
						</Fragment>
					))}
					{props.dataGuidanceForms?.map((df, i) => (
						<FormCard
							key={i}
							{...df}
							isFormOwner={props.caseInformation?.isFormOwner}
							isFormCreatable={props.isFormsCreatable}
						/>
					))}
				</Forms>
			</Paper>
			<Paper classes={{ rounded: bodyRoot }} style={{ marginBottom: "48px" }}>
				<RecordsCard
					records={props.records}
					caseId={props.caseInformation?.caseId}
					title={"records"}
					currency={props.currency}
					isDrawerOpen={props.isDrawerOpen}
					canUserViewFinancials={props.canUserViewFinancials}
					refetch={props.refetch}
				/>
			</Paper>
		</>
	);
};

export default Body;
